import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";


const AppDialogContext = createContext(null);

let externalApi = {
  alert: async (message) => window.alert(String(message || "")),
  confirm: async (message) => window.confirm(String(message || "")),
};

export const appAlert = (message, options = {}) => externalApi.alert(message, options);
export const appConfirm = (message, options = {}) => externalApi.confirm(message, options);

const APP_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo192.png`;

const normalizeMessage = (message) => {
  if (message == null) return "";
  if (typeof message === "string") return message;
  return String(message);
};

const isTranslationKey = (value) => typeof value === "string" && /^[a-z0-9_.-]+$/i.test(value);
const buildTranslator = (t) => (value, vars) => {
  const text = normalizeMessage(value);
  if (!text) return "";
  return isTranslationKey(text) ? t(text, vars) : text;
};

export function AppDialogProvider({ children }) {
  const { t, lang } = useLanguage();
  const dt = useMemo(() => buildTranslator(t), [t]);
  const [dialog, setDialog] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const closeToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = null;
    setToast(null);
  }, []);

  const showToast = useCallback((message, options = {}) => {
    const text = dt(message, options.vars);
    if (!text) return Promise.resolve(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({
      message: text,
      type: options.type || "info",
      title: dt(options.title, options.titleVars),
    });
    toastTimerRef.current = setTimeout(() => setToast(null), options.duration || 2800);
    return Promise.resolve(true);
  }, [dt]);

  const showConfirm = useCallback((message, options = {}) => new Promise((resolve) => {
    setDialog({
      mode: "confirm",
      title: dt(options.title || "dialog.confirmTitle") || (lang === "en" ? "Confirm" : "Onay"),
      message: dt(message, options.vars),
      confirmText: dt(options.confirmText || "dialog.confirm") || (lang === "en" ? "Confirm" : "Onayla"),
      cancelText: dt(options.cancelText || "dialog.cancel") || (lang === "en" ? "Cancel" : "Vazgeç"),
      danger: !!options.danger,
      resolve,
    });
  }), [dt, lang]);

  const showAlert = useCallback((message, options = {}) => {
    if (options.modal) {
      return new Promise((resolve) => {
        setDialog({
          mode: "alert",
          title: dt(options.title || "dialog.infoTitle") || (lang === "en" ? "Info" : "Bilgi"),
          message: dt(message, options.vars),
          confirmText: dt(options.confirmText || "dialog.ok") || (lang === "en" ? "OK" : "Tamam"),
          cancelText: "",
          danger: false,
          resolve,
        });
      });
    }
    return showToast(message, options);
  }, [dt, lang, showToast]);

  const settle = useCallback((value) => {
    setDialog((current) => {
      current?.resolve?.(value);
      return null;
    });
  }, []);

  useEffect(() => {
    externalApi = { alert: showAlert, confirm: showConfirm };
    return () => {
      externalApi = {
        alert: async (message) => window.alert(String(message || "")),
        confirm: async (message) => window.confirm(String(message || "")),
      };
    };
  }, [showAlert, showConfirm]);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const value = useMemo(() => ({ showAlert, showConfirm, showToast }), [showAlert, showConfirm, showToast]);

  return (
    <AppDialogContext.Provider value={value}>
      {children}

      {toast && (
        <div className={`appToast appToast--${toast.type}`} role="status" aria-live="polite">
          <div className="appToastIcon" aria-hidden="true"><img src={APP_LOGO_SRC} alt="" /></div>
          <div className="appToastText">
            {toast.title && <strong>{toast.title}</strong>}
            <span>{toast.message}</span>
          </div>
          <button type="button" className="appToastClose" onClick={closeToast} aria-label={t("dialog.close") || "Close"}>×</button>
        </div>
      )}

      {dialog && (
        <div className="appDialogOverlay" role="presentation" onMouseDown={() => dialog.mode === "alert" && settle(true)}>
          <div className="appDialogCard" role="dialog" aria-modal="true" aria-label={dialog.title} onMouseDown={(e) => e.stopPropagation()}>
            <div className={`appDialogIcon ${dialog.danger ? "danger" : ""}`} aria-hidden="true"><img src={APP_LOGO_SRC} alt="" /></div>
            <div className="appDialogContent">
              <h3>{dialog.title}</h3>
              <p>{dialog.message}</p>
            </div>
            <div className="appDialogActions">
              {dialog.mode === "confirm" && (
                <button type="button" className="appDialogBtn ghost" onClick={() => settle(false)}>{dialog.cancelText}</button>
              )}
              <button type="button" className={`appDialogBtn ${dialog.danger ? "danger" : "primary"}`} onClick={() => settle(true)}>{dialog.confirmText}</button>
            </div>
          </div>
        </div>
      )}
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) return { showAlert: appAlert, showConfirm: appConfirm, showToast: appAlert };
  return ctx;
}
